import type { agentCursorDisplayStatus } from "@/components/cursors/agent-cursor-display-status";
import { asksDesktopApproval } from "@/components/cursors/agent-cursor-intent";
import type { WorkToastItem } from "@/stores/work-toast-store";
import type { AgentCursor } from "@/stores/canvas-store";
import { translate } from "@/i18n";
import { compactCursorMessages } from "@/i18n/messages/compact-cursor";

export type PillContent =
  | { kind: "thinking"; text: string }
  | { kind: "tool"; text: string; failed: boolean }
  | { kind: "reply"; text: string; terminal: boolean }
  | { kind: "error"; text: string }
  | { kind: "question"; item: WorkToastItem }
  | { kind: "confirm"; label: string };

export function compactPillContentKey(content: PillContent | null): string {
  if (!content) return "empty";
  if (content.kind === "question") {
    return `question:${content.item.question?.awaitingInputId ?? content.item.key}`;
  }
  if (content.kind === "reply") {
    return `reply:${content.terminal ? "final" : "stream"}:${content.text}`;
  }
  if (content.kind === "tool") {
    return `tool:${content.failed ? "failed" : "running"}:${content.text}`;
  }
  return `${content.kind}:${content.kind === "confirm" ? content.label : content.text}`;
}

/** Pure projection: questions are visible only while a live toast owns them. */
export function compactPillContent(
  cursor: AgentCursor,
  displayStatus: ReturnType<typeof agentCursorDisplayStatus>,
  questionToast: WorkToastItem | null,
  hiddenQuestionIds: ReadonlySet<string>,
): PillContent | null {
  const questionId = displayStatus.question?.awaitingInputId;
  if (questionId) {
    if (hiddenQuestionIds.has(questionId)) return null;
    return questionToast?.question?.awaitingInputId === questionId &&
      !questionToast.dismissed
      ? { kind: "question", item: questionToast }
      : null;
  }
  if (displayStatus.error) return { kind: "error", text: displayStatus.error };
  if (displayStatus.toolCall) {
    return {
      kind: "tool",
      text: displayStatus.toolCall,
      failed: !!displayStatus.toolFailed,
    };
  }
  const text = displayStatus.contentFull ?? displayStatus.content;
  if (text) return { kind: "reply", text, terminal: !!cursor.frozen };
  if (displayStatus.thinking && !cursor.frozen) {
    return {
      kind: "thinking",
      text: translate(compactCursorMessages, "thinking"),
    };
  }
  return null;
}

/**
 * The work toast a cursor's question pill shows and answers through. A
 * desktop approval asks at the cursor whatever the tray did with its toast:
 * a dismissed toast still asks here, and before this window holds the toast
 * the cursor's own question stands in for it, answered in `projectId` when
 * the cursor does not name its project.
 */
export function cursorQuestionItem(
  cursor: AgentCursor,
  toast: WorkToastItem | null,
  projectId: string | null,
): WorkToastItem | null {
  const question = cursor.question;
  if (!question || !asksDesktopApproval(cursor)) return toast;
  if (toast?.question?.awaitingInputId === question.awaitingInputId) {
    return toast.dismissed ? { ...toast, dismissed: false } : toast;
  }
  return {
    key: cursor.executionId ?? cursor.agentId,
    source: "agent",
    tracked: false,
    state: "question",
    stateBeforeBlocked: null,
    agentName: cursor.name,
    projectId: cursor.projectId ?? projectId,
    conversationId: null,
    taskId: cursor.taskId ?? null,
    nativeSubagentId: null,
    detail: question.question,
    resultText: null,
    question: {
      awaitingInputId: question.awaitingInputId,
      question: question.question,
      context: null,
      options: question.options,
    },
    approval: null,
    stateChangedAt: 0,
    dismissed: false,
  };
}
